"use client";

import { useState, useMemo } from "react";

function heatBg(pct) {
  if (pct === 0) return "transparent";
  if (pct >= 80) return "#fca5a5"; // red-300
  if (pct >= 50) return "#fcd34d"; // amber-300
  if (pct >= 30) return "#6ee7b7"; // emerald-300
  if (pct >= 10) return "#a7f3d0"; // emerald-200
  return "#d1fae5"; // emerald-100
}

function heatText(pct) {
  if (pct >= 80) return "#7f1d1d";
  if (pct >= 50) return "#78350f";
  if (pct >= 30) return "#065f46";
  if (pct >= 10) return "#065f46";
  return "#065f46";
}


const SHORT = {
  "⁠Delay in our repayment": "Delay Repayment",
  "Delinquency In Past/ Bad Credit History": "Bad Credit",
  "Forged Docs": "Forged Docs",
  "Improper Profile/Docs": "Improper Docs",
  "Low company profile/proprietorship firm": "Low Co. Profile",
  "Low Company Vintage": "Low Vintage",
  "Low sal/low profile": "Low Salary",
  "Mode of payment not fixed (cash, tpt, imps)": "Payment Mode",
  "Negative Profile (Delhi Police, politically exposed)": "Negative Profile",
  "⁠OD account": "OD Account",
  "Overdue msgs/Legal msgs": "Overdue/Legal",
  "Salary date not fixed": "Salary Date",
  "Self Employed/Part Time/Contractual based employment": "Self Employed",
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function RejectionHeatmapTable({ data }) {
  const [view, setView] = useState("count");
  const [filterReason, setFilterReason] = useState("All");
  const [sortBy, setSortBy] = useState("total");

  // Derived lists
  const allReasons = useMemo(
    () => Array.from(new Set(data.map((d) => d.reason))).sort(),
    [data],
  );

  const visibleReasons = useMemo(
    () => (filterReason === "All" ? allReasons : [filterReason]),
    [allReasons, filterReason],
  );

  // Build pivot map: state → reason → { count, percentage }
  const pivot = useMemo(() => {
    const map = {};
    for (const row of data) {
      if (!map[row.state]) map[row.state] = {};
      map[row.state][row.reason] = {
        count: row.count,
        percentage: row.percentage,
      };
    }
    return map;
  }, [data]);

  const allStates = useMemo(() => {
    const states = Array.from(new Set(data.map((d) => d.state)));
    if (sortBy === "state") return states.sort();
    // sort by total count desc
    return states.sort((a, b) => {
      const sumA = Object.values(pivot[a] ?? {}).reduce(
        (s, v) => s + v.count,
        0,
      );
      const sumB = Object.values(pivot[b] ?? {}).reduce(
        (s, v) => s + v.count,
        0,
      );
      return sumB - sumA;
    });
  }, [data, pivot, sortBy]);

  // Row totals
  const rowTotal = (state) =>
    Object.values(pivot[state] ?? {}).reduce((s, v) => s + v.count, 0);

  // Grand total
  const grandTotal = allStates.reduce((s, st) => s + rowTotal(st), 0);

  // ── Shared cell style ──
  const tdBase = {
    padding: "10px 12px",
    borderBottom: "1px solid #f3f4f6",
    borderRight: "1px solid #f3f4f6",
    fontSize: "clamp(12px, 0.75vw, 14px)",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  };

  const thBase = {
    ...tdBase,
    fontWeight: 600,
    background: "#f9fafb",
    color: "#374151",
  };

  return (
    <div style={{ width: "100%", backgroundColor: "#fff", border: "1px solid #00000026", borderRadius: "10px" ,  padding:'20px 0px 0px 0px'  }}>
      {/* ── Toolbar ── */}
      <style>{`
        @media (min-width: 1800px) {
          .rh-table td, .rh-table th { font-size: 16px !important; }
        }
        @media (min-width: 2400px) {
          .rh-table td, .rh-table th { font-size: 19px !important; }
        }
        .rh-row:hover td { background: #f0fdf4 !important; }
      `}</style>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "12px",
          marginBottom: "14px",
          padding: "0 20px",
        }}
      >
        {/* View toggle */}
        <div
          style={{
            display: "flex",
            gap: "4px",
            background: "#f3f4f6",
            borderRadius: "8px",
            padding: "4px",
          }}
        >
          {["count", "percentage"].map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              style={{
                padding: "5px 14px",
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 500,
                background: view === v ? "#fff" : "transparent",
                color: view === v ? "#249578" : "#9ca3af",
                boxShadow: view === v ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                transition: "all 0.2s",
              }}
            >
              {v === "count" ? (
                <>
                  <i className="fa fa-hashtag" style={{ marginRight: 6 }} />
                  Count
                </>
              ) : (
                <>
                  <i className="fa fa-percent" style={{ marginRight: 6 }} />%
                </>
              )}
            </button>
          ))}
        </div>

        {/* Reason filter */}
        <select
          value={filterReason}
          onChange={(e) => setFilterReason(e.target.value)}
          style={{
            padding: "6px 12px",
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
            fontSize: "13px",
            color: "#374151",
            background: "#fff",
            cursor: "pointer",
          }}
        >
          <option value="All">All reasons</option>
          {allReasons.map((r) => (
            <option key={r} value={r}>
              {SHORT[r] ?? r}
            </option>
          ))}
        </select>

        {/* Sort toggle */}
        <div
          style={{
            display: "flex",
            gap: "4px",
            background: "#f3f4f6",
            borderRadius: "8px",
            padding: "4px",
          }}
        >
          {["total", "state"].map((s) => (
            <button
              key={s}
              onClick={() => setSortBy(s)}
              style={{
                padding: "5px 12px",
                borderRadius: "6px",
                border: "none",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 500,
                background: sortBy === s ? "#fff" : "transparent",
                color: sortBy === s ? "#249578" : "#9ca3af",
                boxShadow: sortBy === s ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                transition: "all 0.2s",
              }}
            >
              {s === "total" ? "Sort: Volume" : "Sort: A–Z"}
            </button>
          ))}
        </div>

        {/* Legend */}
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: "8px",
            alignItems: "center",
          }}
        >
          {[
            { label: "< 10%", bg: "#d1fae5", tx: "#065f46" },
            { label: "10–30%", bg: "#a7f3d0", tx: "#065f46" },
            { label: "30–50%", bg: "#6ee7b7", tx: "#065f46" },
            { label: "50–80%", bg: "#fcd34d", tx: "#78350f" },
            { label: "≥ 80%", bg: "#fca5a5", tx: "#7f1d1d" },
          ].map(({ label, bg, tx }) => (
            <span
              key={label}
              style={{
                background: bg,
                color: tx,
                fontSize: "11px",
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: "999px",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* ── Table ── */}
      <div
        style={{
          overflowX: "auto",
          borderRadius: "10px",
          border: "1px solid #e5e7eb",

        }}
      >
        <table
          className="rh-table"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <colgroup>
            <col style={{ width: "140px" }} />
            {visibleReasons.map((r) => (
              <col key={r} style={{ width: "110px" }} />
            ))}
            <col style={{ width: "80px" }} />
          </colgroup>

          <thead>
            <tr>
              <th
                style={{
                  ...thBase,
                  textAlign: "left",
                  position: "sticky",
                  left: 0,
                  zIndex: 2,
                }}
              >
                State
              </th>
               <th style={{ ...thBase, textAlign: "center", color: "#249578",  position: "sticky",
                  left: '138px',
                  zIndex: 2, }}>
                Total
              </th>
              {visibleReasons.map((r) => (
                <th
                  key={r}
                  style={{
                    ...thBase,
                    textAlign: "center",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={r}
                >
                  {SHORT[r] ?? r}
                </th>
              ))}
             
            </tr>
          </thead>

          <tbody>
            {allStates.map((state) => {
              const total = rowTotal(state);
              return (
                <tr key={state} className="rh-row">
                  {/* Sticky state cell */}
                  <td
                    style={{
                      ...tdBase,
                      fontWeight: 600,
                      color: "#111827",
                      background: "#fff",
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                    }}
                  >
                    {state}
                  </td>
                   <td
                    style={{
                      ...tdBase,
                      textAlign: "center",
                      fontWeight: 700,
                      color: "#249578",
                      background: "#f0fdf4",
                       position: "sticky",
                      left: '138px',
                      zIndex: 1,
                    }}
                  >
                    {total}
                  </td>

                  {visibleReasons.map((r) => {
                    const cell = pivot[state]?.[r];
                    const pct = cell?.percentage ?? 0;
                    const val = cell
                      ? view === "count"
                        ? cell.count
                        : `${cell.percentage.toFixed(1)}%`
                      : "—";

                    return (
                      <td
                        key={r}
                        style={{
                          ...tdBase,
                          textAlign: "center",
                          background: cell ? heatBg(pct) : "transparent",
                          color: cell ? heatText(pct) : "#d1d5db",
                          fontWeight: cell ? 600 : 400,
                        }}
                      >
                        {val}
                      </td>
                    );
                  })}

                  {/* Row total */}
                 
                </tr>
              );
            })}
          </tbody>

          {/* Grand total row */}
          <tfoot>
            <tr style={{ background: "#f9fafb" }}>
              <td
                style={{
                  ...tdBase,
                  fontWeight: 700,
                  color: "#111827",
                  position: "sticky",
                  left: 0,
                  background: "#f9fafb",
                  zIndex: 1,
                }}
              >
                Grand Total
              </td>
              {visibleReasons.map((r) => {
                const colTotal = data
                  .filter((d) => d.reason === r)
                  .reduce((s, d) => s + d.count, 0);
                return (
                  <td
                    key={r}
                    style={{
                      ...tdBase,
                      textAlign: "center",
                      fontWeight: 700,
                      color: "#374151",
                    }}
                  >
                    {colTotal}
                  </td>
                );
              })}
              <td
                style={{
                  ...tdBase,
                  textAlign: "center",
                  fontWeight: 700,
                  color: "#249578",
                  background: "#dcfce7",
                }}
              >
                {grandTotal}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
